import { Text, palette } from "@minervaproject/fds";
import clsx from "clsx";
import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

import { CHART_COLORS } from "components/stage/poll_session_pane/poll_chart";
import { useArraySelector } from "hooks/redux_hooks";
import {
  WorkbookComponentTypeType,
  WorkbookQuestionType,
  WorkbookQuestionTypeType,
  WorkbookType,
} from "shared/types/workbook";
import { workbookSelectors } from "store";

import "./workbook_questions.scss";

const QUESTION_TYPE_TO_DISPLAY_STRING = {
  [WorkbookQuestionTypeType.Code]: "Code",
  [WorkbookQuestionTypeType.File]: "File Upload",
  [WorkbookQuestionTypeType.Instructions]: "", // should never be displayed
  [WorkbookQuestionTypeType.LongForm]: "Long Form",
  [WorkbookQuestionTypeType.MultipleChoice]: "Multiple Choice",
  [WorkbookQuestionTypeType.Table]: "Table",
};
const RED_THRESHOLD = 33;
const GREEN_THRESHOLD = 67;

function LegendItem({ color, label, percent }: { color: string; label: string; percent: number }) {
  return (
    <div className="flex items-center">
      <div className="workbook-legend-circle mr1" style={{ backgroundColor: color }} />
      <Text kind="body-s" color="white" bold style={{ width: "45px" }}>
        {percent}%
      </Text>
      <Text kind="body-s truncate" style={{ maxWidth: "80%" }}>
        {label}
      </Text>
    </div>
  );
}

function WorkbookMultipleChoiceChart({
  question,
  workbooks,
}: {
  question: WorkbookQuestionType;
  workbooks: WorkbookType[];
}) {
  const choicesComponent = question.components.find(
    (component) => component.type === WorkbookComponentTypeType.ChoicesField,
  );

  if (!choicesComponent) return null;

  const isMultipleResponse = choicesComponent.multiple;
  const data = question.multiple_choice_data.map(
    (choice: { value: string; label: string }, index: number) => ({
      id: index,
      choice: choice.label,
      count: workbooks.filter((workbook: WorkbookType) => {
        const selectedChoices = workbook.answers[question.uuid][choicesComponent.uuid];
        if (!Array.isArray(selectedChoices)) return false;
        if (selectedChoices) {
          return selectedChoices.includes(parseInt(choice.value, 10));
        }
        return false;
      }).length,
    }),
  );
  const isEmpty = data.every((item) => item.count === 0);

  if (isMultipleResponse) {
    return (
      <div className="mt4">
        {data.map((item, index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];
          const percent = (item.count / workbooks.length) * 100;
          return (
            <div key={item.id} className="flex items-center mb2">
              <div className="workbook-bar-chart-container mr4">
                <div
                  className="workbook-bar-chart-fill"
                  style={{
                    width: `${percent}px`,
                    backgroundColor: color,
                  }}
                />
              </div>
              <LegendItem color={color} label={item.choice} percent={percent} />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center mt4">
      <ResponsiveContainer width={150} height={150}>
        <PieChart>
          {isEmpty ? (
            <Pie
              data={[{ choice: "no data", count: 1 }]}
              dataKey="count"
              innerRadius="50%"
              outerRadius="90%"
              fill={palette.blackTint90}
              stroke="none"
            />
          ) : (
            <Pie data={data} dataKey="count" innerRadius="50%" outerRadius="90%" stroke="none">
              {data.map((item, index) => (
                <Cell key={`cell-${item.id}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
          )}
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-auto ml4">
        {data.map((item, index) => (
          <div key={item.id} className="mb2">
            <LegendItem
              color={CHART_COLORS[index % CHART_COLORS.length]}
              label={item.choice}
              percent={(item.count / workbooks.length) * 100}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkbookQuestions({
  flavor,
  hasOneResourceForAllFlavorsAndGroups = false,
}: {
  flavor?: string;
  hasOneResourceForAllFlavorsAndGroups?: boolean;
}) {
  const questions = useArraySelector(workbookSelectors.selectQuestionsByFlavor());
  const workbooks = useArraySelector(
    workbookSelectors.selectWorkbooksByFlavor(
      hasOneResourceForAllFlavorsAndGroups ? undefined : flavor,
    ),
  );

  if (!workbooks.length) return null;

  const questionUuids = Object.keys(workbooks[0].answers);
  const filteredQuestions = questions.filter((question) => questionUuids.includes(question.uuid));

  const getPercentAnsweredForQuestion = (questionUuid: string) => {
    const completeValues = workbooks.map((workbook) => {
      const answer = workbook.answers[questionUuid];
      const content = answer && Object.values(answer)[0];
      if (Array.isArray(content)) {
        if (content.length === 0) {
          return 0;
        }
        // Poll answers will be an array of selected indexes, like [1, 2]
        if (content.every(Number.isInteger)) {
          return 1;
        }
        // For tables, we calculate the percentage of cells that have been started
        return content.filter((cell) => cell !== null).length / content.length;
      }
      // For all other question types, we return 1 for started,
      // or 0 for not yet started
      return content ? 1 : 0;
    });
    const completeSum = completeValues.reduce((acc, percentage) => acc + percentage, 0);
    return completeSum ? Math.round((completeSum / workbooks.length) * 100) : 0;
  };

  return (
    <table className="w-full">
      <thead className="bg-black-tint-20 bold left-align">
        <tr>
          <th className="p4">#</th>
          <th className="p4">Question</th>
          <th className="p4">% of Groups Answered</th>
        </tr>
      </thead>
      <tbody>
        {filteredQuestions.map((question, index) => {
          const percentAnswered = getPercentAnsweredForQuestion(question.uuid);
          return (
            <tr key={question.uuid} style={{ borderBottom: "1px solid var(--black-tint-40)" }}>
              <td className="p4 align-middle" style={{ width: "75px" }}>
                {index + 1}
              </td>
              <td className="p4" style={{ width: "600px", maxWidth: "600px" }}>
                <Text className="mb2" kind="body-s" bold>
                  {QUESTION_TYPE_TO_DISPLAY_STRING[question.type]}
                </Text>
                <Text
                  className="truncate-five-lines"
                  kind="body-s"
                  color="white"
                  style={{ maxWidth: "600px" }}
                >
                  {question.question}
                </Text>
                {question.type === WorkbookQuestionTypeType.MultipleChoice && (
                  <WorkbookMultipleChoiceChart question={question} workbooks={workbooks} />
                )}
              </td>
              <td className="p4 align-middle">
                <div className="flex items-center">
                  <div style={{ width: "45px" }}>{percentAnswered}%</div>
                  <div className="workbook-bar-chart-container">
                    <div
                      className={clsx("workbook-bar-chart-fill", {
                        "bg-red": percentAnswered <= RED_THRESHOLD,
                        "bg-green": percentAnswered >= GREEN_THRESHOLD,
                        "bg-orange":
                          percentAnswered > RED_THRESHOLD && percentAnswered < GREEN_THRESHOLD,
                      })}
                      style={{ width: `${percentAnswered}px` }}
                    />
                  </div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export { LegendItem, WorkbookMultipleChoiceChart, WorkbookQuestions };
